import { templatePath } from '../path.ts'
import type { ChainEntry, ElementContext, Position } from '../contextTypes.ts'

export type { Box, ChainEntry, ElementContext, Position } from '../contextTypes.ts'

const MAX_CHAIN = 5
const MAX_DEPTH = 40
const LABEL_REACH = 3
const ATTRIBUTE_ALLOWLIST = ['role', 'type', 'aria-selected', 'aria-expanded', 'aria-current', 'aria-checked', 'data-state']
// alt, aria-label and title can be built from data ("Invoice 42 for Acme AB"), so they pass only as short plain words.
const SAFE_TEXT_ATTRIBUTES = ['alt', 'aria-label', 'title']
const SAFE_TEXT = /^[A-Za-zÅÄÖåäö][A-Za-zÅÄÖåäö .&'-]{0,39}$/

function annotated(node: Element): ChainEntry | null {
  const component = node.getAttribute('data-sentry-component')
  if (!component) return null
  return {
    component,
    file: node.getAttribute('data-sentry-source-file') ?? '',
    element: node.getAttribute('data-sentry-element') ?? node.tagName.toLowerCase(),
  }
}

const sameEntry = (a: ChainEntry, b: ChainEntry) => a.component === b.component && a.file === b.file && a.element === b.element

function buildChain(start: Element): { chain: ChainEntry[]; first: Element | null } {
  const chain: ChainEntry[] = []
  let first: Element | null = null
  let node: Element | null = start
  for (let depth = 0; node && depth < MAX_DEPTH && chain.length < MAX_CHAIN; depth++, node = node.parentElement) {
    const entry = annotated(node)
    if (!entry) continue
    const previous = chain[chain.length - 1]
    if (previous && previous.component === entry.component && previous.file === entry.file) continue
    first ??= node
    chain.push(entry)
  }
  return { chain, first }
}

function instancePosition(node: Element): Position {
  const entry = annotated(node)
  const parent = node.parentElement
  if (!entry || !parent) return {}
  const siblings = Array.from(parent.children).filter((child) => {
    const other = annotated(child)
    return other !== null && sameEntry(other, entry)
  })
  return siblings.length > 1 ? { n: siblings.indexOf(node) + 1, of: siblings.length } : {}
}

function tablePosition(start: Element): Position {
  const cell = start.closest('td, th')
  const row = cell?.closest('tr')
  if (!cell || !row) return {}
  const rowParent = row.parentElement
  const rows = rowParent ? Array.from(rowParent.children).filter((child) => child.tagName === 'TR') : []
  const cells = Array.from(row.children)
  return { row: rows.indexOf(row) + 1, rows: rows.length, col: cells.indexOf(cell) + 1, cols: cells.length }
}

function findAttributeUpward(start: Element, name: string, reach: number): string | undefined {
  let node: Element | null = start
  for (let depth = 0; node && depth <= reach; depth++, node = node.parentElement) {
    const value = node.getAttribute(name)
    if (value) return value
  }
  return undefined
}

const CONTROL = 'button, a, input, select, textarea, summary, [role="button"], [role="menuitem"], [role="tab"], [role="link"], [role="option"]'

/**
 * A menu, dialog or popover renders in a portal, outside the page's component tree, so a click inside it has no
 * annotated ancestor. Radix links such content to the control that opened it by id, which gives the chain back.
 */
function openerOf(start: Element): Element | null {
  let node: Element | null = start
  for (let depth = 0; node && depth < MAX_DEPTH; depth++, node = node.parentElement) {
    const labelledBy = node.getAttribute('aria-labelledby')
    if (labelledBy) {
      const owner = labelledBy.split(' ').map((id) => document.getElementById(id)).find((candidate) => candidate && !node?.contains(candidate))
      if (owner) return owner
    }
    const id = node.id
    if (id) {
      const owner = Array.from(document.querySelectorAll('[aria-controls]')).find((candidate) => candidate.getAttribute('aria-controls')?.split(' ').includes(id))
      if (owner) return owner
    }
  }
  return null
}

function safeAttributes(el: Element): Record<string, string> {
  const attrs: Record<string, string> = {}
  for (const name of ATTRIBUTE_ALLOWLIST) {
    const value = el.getAttribute(name)
    if (value !== null && value.length <= 40) attrs[name] = value
  }
  for (const name of SAFE_TEXT_ATTRIBUTES) {
    const value = el.getAttribute(name)
    if (value !== null && SAFE_TEXT.test(value)) attrs[name] = value
  }
  const href = el.getAttribute('href')
  if (href) attrs.href = templatePath(href)
  return attrs
}

export function describeContext(el: Element): ElementContext {
  let { chain, first } = buildChain(el)
  let viaTrigger = false
  if (chain.length === 0) {
    const opener = openerOf(el)
    if (opener) {
      ;({ chain, first } = buildChain(opener))
      viaTrigger = chain.length > 0
    }
  }
  const position = { ...(first ? instancePosition(first) : {}), ...tablePosition(el) }
  const label = findAttributeUpward(el, 'data-sat-label', LABEL_REACH)
  const labelKey = findAttributeUpward(el, 'data-sat-key', LABEL_REACH)
  const src = findAttributeUpward(el, 'data-sat-src', LABEL_REACH)
  const attrs = safeAttributes(el.closest(CONTROL) ?? el)
  const rect = el.getBoundingClientRect()
  const ctx: ElementContext = { tag: el.tagName.toLowerCase() }
  if (chain.length > 0) ctx.chain = chain
  if (viaTrigger) ctx.viaTrigger = true
  if (Object.keys(position).length > 0) ctx.position = position
  if (label) ctx.label = label
  if (labelKey) ctx.labelKey = labelKey
  if (src) ctx.src = src
  if (Object.keys(attrs).length > 0) ctx.attrs = attrs
  ctx.box = { x: Math.round(rect.x), y: Math.round(rect.y), w: Math.round(rect.width), h: Math.round(rect.height) }
  return ctx
}
